import * as React from 'react';
import apiService, { Project, SubProject } from '../../services/api';
import { RefreshCw, ChevronDown } from 'lucide-react';
import type { SolutionId } from './SolutionsGrid';
import './ProjectSelector.css';

/** Domain/sub_domain filter and labels per solution (matches backend project types). */
const SOLUTION_CONFIG: Record<SolutionId, { domain: string; sub_domain: string; projectLabel: string; subProjectLabel: string }> = {
  master_cross_checks: {
    domain: 'Real Estate',
    sub_domain: 'Master Cross Checks',
    projectLabel: 'Project',
    subProjectLabel: 'Sub-project',
  },
  construction_loan: {
    domain: 'Real Estate',
    sub_domain: 'Construction Loan Agreement',
    projectLabel: 'Project',
    subProjectLabel: 'Sub-project',
  },
};

interface ProjectSelectorProps {
  solutionId: SolutionId;
  onSubProjectSelect: (subProjectId: string) => void;
  onProjectSelect?: (projectId: string) => void;
  onCreateNewSubProject?: () => void;
  selectedSubProjectId?: string;
  selectedProjectId?: string;
  refreshTrigger?: number;
  isAuthenticated?: boolean;
  /** When true (e.g. MCC / CLA guided client create), collapse the panel to free vertical space. */
  guidedSetupActive?: boolean;
}

export const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  solutionId,
  onSubProjectSelect,
  onProjectSelect,
  onCreateNewSubProject,
  selectedSubProjectId,
  selectedProjectId,
  refreshTrigger,
  isAuthenticated = true,
  guidedSetupActive = false,
}) => {
  const [projects, setProjects] = React.useState<Project[]>([]);
  const [subProjects, setSubProjects] = React.useState<SubProject[]>([]);
  const [internalSelectedProjectId, setInternalSelectedProjectId] = React.useState<string>('');
  // Separate flags so an in-flight sub-project fetch never disables the project
  // dropdown (and vice-versa). A single shared flag could get stuck `true` during
  // auth/session races, leaving both selectors permanently greyed out.
  const [loadingProjects, setLoadingProjects] = React.useState(false);
  const [loadingSubProjects, setLoadingSubProjects] = React.useState(false);

  const projectsFetchGenRef = React.useRef(0);
  const subProjectsFetchGenRef = React.useRef(0);
  const activeProjectIdForSubRef = React.useRef<string>('');

  // Use prop if provided, otherwise use internal state
  const activeProjectId = selectedProjectId || internalSelectedProjectId;
  activeProjectIdForSubRef.current = activeProjectId;
  const [error, setError] = React.useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = React.useState(false);

  React.useEffect(() => {
    if (guidedSetupActive) {
      setIsCollapsed(true);
    }
  }, [guidedSetupActive]);

  // Load projects when solution, auth, or mount changes
  const loadProjects = React.useCallback(async () => {
    if (!isAuthenticated) {
      setProjects([]);
      setSubProjects([]);
      setError(null);
      return;
    }

    const fetchGen = ++projectsFetchGenRef.current;
    const config = SOLUTION_CONFIG[solutionId];
    try {
      setLoadingProjects(true);
      setError(null);
      const response = await apiService.getProjects();
      if (fetchGen !== projectsFetchGenRef.current) {
        return;
      }
      if (response.success && response.data) {
        const filteredProjects = response.data.filter(
          (project) =>
            project.domain === config.domain && project.sub_domain === config.sub_domain
        );
        setProjects(filteredProjects);
      } else {
        setError('Failed to load projects');
      }
    } catch (err: any) {
      if (fetchGen !== projectsFetchGenRef.current) {
        return;
      }
      console.error('Error loading projects:', err);

      // Provide user-friendly error messages
      if (err.response?.status === 401) {
        setError('Failed to load projects. Please try again.');
      } else if (err.response?.status === 404) {
        setError('Projects endpoint not found. Please verify the backend is running and the API URL is correct.');
      } else if (err.message?.includes('No projects endpoint available')) {
        setError('Unable to connect to projects API. Please check that the backend server is running.');
      } else {
        setError(err.message || 'Failed to load projects');
      }
    } finally {
      if (fetchGen === projectsFetchGenRef.current) {
        setLoadingProjects(false);
      }
    }
  }, [solutionId, isAuthenticated]);

  React.useEffect(() => {
    setProjects([]);
    setSubProjects([]);
    if (!isAuthenticated) {
      // Invalidate any in-flight fetches and clear loading so the selectors are
      // never left disabled when we come back authenticated.
      projectsFetchGenRef.current += 1;
      subProjectsFetchGenRef.current += 1;
      setLoadingProjects(false);
      setLoadingSubProjects(false);
      setError(null);
      return;
    }
    loadProjects();
  }, [loadProjects, isAuthenticated]);

  // Load sub-projects when project is selected, projects are known, and auth is ready
  React.useEffect(() => {
    if (!isAuthenticated) {
      setSubProjects([]);
      setLoadingSubProjects(false);
      setError(null);
      return;
    }

    if (!activeProjectId) {
      setSubProjects([]);
      setLoadingSubProjects(false);
      setError(null);
      return;
    }

    if (projects.length > 0 && !projects.some((project) => project.id === activeProjectId)) {
      setSubProjects([]);
      setLoadingSubProjects(false);
      setError(null);
      return;
    }

    // Wait until the project list has loaded before fetching sub-projects, but
    // never gate on the sub-project loading flag itself (that caused a refetch
    // loop that kept the selectors disabled).
    if (loadingProjects && projects.length === 0) {
      return;
    }

    loadSubProjects(activeProjectId);
  }, [activeProjectId, refreshTrigger, isAuthenticated, projects, loadingProjects]);

  const loadSubProjects = async (projectId: string) => {
    const requestedProjectId = projectId;
    const fetchGen = ++subProjectsFetchGenRef.current;
    try {
      if (!apiService.isAuthenticated()) {
        return;
      }

      setLoadingSubProjects(true);
      setError(null);
      const domainHint =
        solutionId === 'construction_loan' ? 'construction-loan' : 'real-estate';
      const response = await apiService.getSubProjects(projectId, domainHint);
      if (
        fetchGen !== subProjectsFetchGenRef.current ||
        requestedProjectId !== activeProjectIdForSubRef.current
      ) {
        return;
      }
      if (response.success && response.data) {
        setSubProjects(response.data);
      } else {
        setError('Failed to load sub-projects');
      }
    } catch (err: any) {
      if (
        fetchGen !== subProjectsFetchGenRef.current ||
        requestedProjectId !== activeProjectIdForSubRef.current
      ) {
        return;
      }
      console.error('Error loading sub-projects:', err);
      if (err.response?.status === 403) {
        setError('You do not have access to this project.');
      } else if (err.response?.status === 404) {
        setError('Project not found or sub-projects are unavailable for this project.');
      } else {
        setError(err.message || 'Failed to load sub-projects');
      }
    } finally {
      if (
        fetchGen === subProjectsFetchGenRef.current &&
        requestedProjectId === activeProjectIdForSubRef.current
      ) {
        setLoadingSubProjects(false);
      }
    }
  };

  const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const projectId = e.target.value;
    if (!selectedProjectId) {
      // Only update internal state if prop is not provided
      setInternalSelectedProjectId(projectId);
    }
    onSubProjectSelect(''); // Clear sub-project selection
    if (onProjectSelect) {
      onProjectSelect(projectId);
    }
  };

  const handleSubProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === '__create_new__' && onCreateNewSubProject) {
      onSubProjectSelect('');
      onCreateNewSubProject();
      return;
    }
    onSubProjectSelect(value);
  };

  return (
    <div className={`project-selector ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="project-selector-header">
        <div className="project-selector-header-left">
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="collapse-toggle-button"
            title={isCollapsed ? "Expand" : "Collapse"}
          >
            <ChevronDown 
              size={18} 
              className={`collapse-icon ${isCollapsed ? 'collapsed' : ''}`}
            />
          </button>
          <h3>Project Settings</h3>
        </div>
        <button
          onClick={() => loadProjects()}
          className="refresh-button"
          title={
            solutionId === 'master_cross_checks' || solutionId === 'construction_loan'
              ? 'Refresh projects'
              : 'Refresh playbooks'
          }
          disabled={loadingProjects || loadingSubProjects}
        >
          <RefreshCw size={18} />
        </button>
      </div>
      {/* loadingProjects/loadingSubProjects are intentionally split: each selector
          only disables for its own fetch so neither stays stuck disabled. */}

      {!isCollapsed && (
        <>
          {error && <div className="error-message">{error}</div>}

          <div className="project-selector-content">
            <div className="project-selector-left">
              <div className="select-group">
                <label htmlFor="project-select">Select {SOLUTION_CONFIG[solutionId].projectLabel}:</label>
                <select
                  id="project-select"
                  dir="auto"
                  value={activeProjectId}
                  onChange={handleProjectChange}
                  disabled={loadingProjects}
                >
                  <option value="">-- Select a {SOLUTION_CONFIG[solutionId].projectLabel.toLowerCase()} --</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>

              {activeProjectId && (
                <div className="select-group">
                  <label htmlFor="subproject-select">Select {SOLUTION_CONFIG[solutionId].subProjectLabel}:</label>
                  <select
                    id="subproject-select"
                    dir="auto"
                    value={selectedSubProjectId || ''}
                    onChange={handleSubProjectChange}
                    disabled={loadingSubProjects || !activeProjectId}
                  >
                    <option value="">-- Select a {SOLUTION_CONFIG[solutionId].subProjectLabel.toLowerCase()} --</option>
                    {onCreateNewSubProject && (
                      <option value="__create_new__">+ Create new...</option>
                    )}
                    {subProjects.map((subProject) => (
                      <option key={subProject.id} value={subProject.id}>
                        {subProject.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

        </>
      )}
    </div>
  );
};

